import { GameState } from '../game/GameState';
import type { ProvinceEconomyLedger, ProvinceState } from '../types/game';
import { DataRegistry } from './DataRegistry';
import { ModifierManager } from './ModifierManager';
import { TradeManager } from './TradeManager';

const FOOD_GOODS = ['rice', 'fish', 'salt', 'rations'];

export class EconomyManager {
  static readonly instance = new EconomyManager();

  processMonth() {
    const campaign = GameState.instance.requireCampaign();
    const ledgers = new Map<string, ProvinceEconomyLedger>();

    for (const province of Object.values(campaign.provinces)) {
      province.workforce = this.workforceCapacity(province);
      ledgers.set(province.id, this.emptyLedger(province));
    }

    this.balanceInternalMarkets(ledgers);

    for (const province of Object.values(campaign.provinces)) {
      const ledger = ledgers.get(province.id);
      if (!ledger) continue;
      this.runProduction(province, ledger);
      this.consumeAndGrow(province, ledger);
      this.updatePrices(province, ledger);
      this.collectRevenue(province, ledger);
      this.applyCapacityAndEffects(province, ledger);
      province.lastEconomy = ledger;
    }

    TradeManager.instance.processMonth();
    GameState.instance.updateVictoryProgress(DataRegistry.instance);
  }

  workforceCapacity(province: ProvinceState) {
    const groups = province.populationGroups;
    const productive = (groups.peasants ?? 0) + (groups.artisans ?? 0) + (groups.merchants ?? 0) + (groups.soldiers ?? 0) * 0.35;
    const stability = PhaserMathClamp((province.loyalty + province.prosperity + (100 - province.unrest)) / 300, 0.45, 1.05);
    const devastation = 1 - province.devastation / 180;
    return Math.max(1, Math.round(productive * stability * devastation));
  }

  storageCapacity(provinceId: string) {
    const province = GameState.instance.requireCampaign().provinces[provinceId];
    if (!province) return 0;
    let capacity = 80 + (province.portLevel ?? 0) * 20 + (province.roadLevel ?? 0) * 8;
    for (const buildingId of province.buildings) {
      const building = DataRegistry.instance.buildings[buildingId];
      const level = province.buildingLevels[buildingId] ?? 1;
      capacity += (building?.effects?.storageCapacity ?? 0) * level;
    }
    return Math.round(capacity);
  }

  priceFor(provinceId: string, goodId: string) {
    const province = GameState.instance.requireCampaign().provinces[provinceId];
    return province?.marketPrices[goodId] ?? DataRegistry.instance.goods[goodId]?.basePrice ?? 1;
  }

  buildingUtilization(provinceId: string, buildingId: string) {
    const province = GameState.instance.requireCampaign().provinces[provinceId];
    const ledger = province?.lastEconomy;
    const building = DataRegistry.instance.buildings[buildingId];
    if (!province || !ledger || !building) return 1;
    const level = province.buildingLevels[buildingId] ?? 1;
    const expected = Object.values(building.outputs ?? {}).reduce((sum, amount) => sum + amount * level, 0);
    const produced = Object.keys(building.outputs ?? {}).reduce((sum, good) => sum + (ledger.production[good] ?? 0), 0);
    return expected > 0 ? PhaserMathClamp(produced / expected, 0, 1) : 1;
  }

  private emptyLedger(province: ProvinceState): ProvinceEconomyLedger {
    return {
      turn: GameState.instance.requireCampaign().turn,
      workforceCapacity: province.workforce,
      workforceUsed: 0,
      production: {},
      consumption: {},
      imports: {},
      exports: {},
      prices: {},
      taxIncome: 0,
      maintenance: 0,
      corruptionLoss: 0,
      transportCost: 0,
      foodNeed: Math.max(2, Math.ceil(province.population / 11)),
      foodConsumed: 0,
      foodBalance: 0,
      populationChange: 0,
      notes: [],
    };
  }

  private balanceInternalMarkets(ledgers: Map<string, ProvinceEconomyLedger>) {
    const campaign = GameState.instance.requireCampaign();
    const registry = DataRegistry.instance;
    const seen = new Set<string>();

    for (const province of Object.values(campaign.provinces)) {
      const data = registry.provinces[province.id];
      if (!data) continue;
      for (const neighborId of data.neighbors) {
        const pair = [province.id, neighborId].sort().join(':');
        if (seen.has(pair)) continue;
        seen.add(pair);
        const neighbor = campaign.provinces[neighborId];
        if (!neighbor || !this.canExchange(province, neighbor)) continue;

        for (const good of registry.goodList()) {
          this.transferSurplus(province, neighbor, good.id, ledgers);
          this.transferSurplus(neighbor, province, good.id, ledgers);
        }
      }
    }
  }

  private transferSurplus(source: ProvinceState, target: ProvinceState, goodId: string, ledgers: Map<string, ProvinceEconomyLedger>) {
    const sourceTarget = this.targetStock(source, goodId);
    const targetTarget = this.targetStock(target, goodId);
    const surplus = Math.max(0, (source.storage[goodId] ?? 0) - sourceTarget * 1.25);
    const shortage = Math.max(0, targetTarget - (target.storage[goodId] ?? 0));
    if (surplus < 0.5 || shortage < 0.5) return;

    const sourceData = DataRegistry.instance.provinces[source.id];
    const targetData = DataRegistry.instance.provinces[target.id];
    const roadCapacity = 2 + (source.roadLevel ?? 0) * 2 + (target.roadLevel ?? 0) * 2;
    const seaCapacity = sourceData?.port && targetData?.port ? 6 + (source.portLevel ?? 0) + (target.portLevel ?? 0) : 0;
    const amount = round2(Math.min(surplus, shortage, roadCapacity + seaCapacity));
    if (amount <= 0) return;

    source.storage[goodId] = round2((source.storage[goodId] ?? 0) - amount);
    target.storage[goodId] = round2((target.storage[goodId] ?? 0) + amount);
    const sourceLedger = ledgers.get(source.id);
    const targetLedger = ledgers.get(target.id);
    if (sourceLedger) sourceLedger.exports[goodId] = round2((sourceLedger.exports[goodId] ?? 0) + amount);
    if (targetLedger) targetLedger.imports[goodId] = round2((targetLedger.imports[goodId] ?? 0) + amount);

    const price = DataRegistry.instance.goods[goodId]?.basePrice ?? 1;
    const infrastructure = Math.min(0.7, ((source.roadLevel ?? 0) + (target.roadLevel ?? 0)) * 0.08 + seaCapacity * 0.02);
    const cost = round2(amount * price * (0.12 - infrastructure * 0.08));
    const owner = GameState.instance.requireCampaign().factions[target.owner];
    if (owner) owner.treasury = round2(Math.max(0, owner.treasury - cost));
    if (targetLedger) targetLedger.transportCost = round2(targetLedger.transportCost + cost);
  }

  private runProduction(province: ProvinceState, ledger: ProvinceEconomyLedger) {
    const registry = DataRegistry.instance;
    const modifiers = ModifierManager.instance.forFaction(province.owner);
    let workforceRemaining = ledger.workforceCapacity;
    const orderedBuildings = [...province.buildings].sort((left, right) => this.buildingPriority(left) - this.buildingPriority(right));

    for (const buildingId of orderedBuildings) {
      const building = registry.buildings[buildingId];
      if (!building) continue;
      const level = province.buildingLevels[buildingId] ?? 1;
      const workforceNeed = Math.max(0, (building.workforce ?? 4) * level);
      const workforceRatio = workforceNeed > 0 ? Math.min(1, workforceRemaining / workforceNeed) : 1;
      let inputRatio = 1;
      for (const [good, amount] of Object.entries(building.inputs ?? {})) {
        const required = amount * level;
        if (required > 0) inputRatio = Math.min(inputRatio, (province.storage[good] ?? 0) / required);
      }
      const utilization = PhaserMathClamp(Math.min(workforceRatio, inputRatio), 0, 1);
      const workforceUsed = Math.round(workforceNeed * utilization);
      workforceRemaining = Math.max(0, workforceRemaining - workforceUsed);
      ledger.workforceUsed += workforceUsed;
      ledger.maintenance = round2(ledger.maintenance + (building.maintenanceGold ?? 0) * level);

      if (utilization < 0.25 && Object.keys(building.outputs ?? {}).length) {
        ledger.notes.push(`${building.name} is operating below 25%.`);
      }

      for (const [good, amount] of Object.entries(building.inputs ?? {})) {
        const consumed = round2(amount * level * utilization);
        province.storage[good] = round2(Math.max(0, (province.storage[good] ?? 0) - consumed));
        ledger.consumption[good] = round2((ledger.consumption[good] ?? 0) + consumed);
      }
      for (const [good, amount] of Object.entries(building.outputs ?? {})) {
        const foodBonus = FOOD_GOODS.includes(good) ? modifiers.foodOutputBonus : 0;
        const output = round2((amount * level + foodBonus) * utilization * (1 + province.prosperity / 250) * (1 - province.devastation / 140));
        province.storage[good] = round2((province.storage[good] ?? 0) + output);
        ledger.production[good] = round2((ledger.production[good] ?? 0) + output);
      }

      const effects = building.effects ?? {};
      province.unrest = PhaserMathClamp(province.unrest - (effects.unrestReduction ?? 0) * level, 0, 100);
      province.loyalty = PhaserMathClamp(province.loyalty + (effects.loyalty ?? 0) * level, 0, 100);
      province.prosperity = PhaserMathClamp(province.prosperity + (effects.prosperity ?? 0) * level, 0, 100);
      province.corruption = PhaserMathClamp(province.corruption - (effects.corruptionReduction ?? 0) * level, 0, 100);
    }
  }

  private consumeAndGrow(province: ProvinceState, ledger: ProvinceEconomyLedger) {
    let remainingNeed = ledger.foodNeed;
    for (const goodId of ['rice', 'fish', 'rations', 'salt']) {
      const available = province.storage[goodId] ?? 0;
      const used = round2(Math.min(available, remainingNeed));
      province.storage[goodId] = round2(Math.max(0, available - used));
      ledger.consumption[goodId] = round2((ledger.consumption[goodId] ?? 0) + used);
      ledger.foodConsumed = round2(ledger.foodConsumed + used);
      remainingNeed = round2(Math.max(0, remainingNeed - used));
    }

    ledger.foodBalance = round2(ledger.foodConsumed - ledger.foodNeed);
    const ratio = ledger.foodNeed > 0 ? ledger.foodConsumed / ledger.foodNeed : 1;
    const priorPopulation = province.population;
    if (ratio >= 1) {
      const surplus = Math.min(1.5, ratio - 1);
      province.foodSecurity = PhaserMathClamp(province.foodSecurity + 5 + surplus * 5, 0, 100);
      province.prosperity = PhaserMathClamp(province.prosperity + 1 + surplus, 0, 100);
      province.unrest = PhaserMathClamp(province.unrest - 1, 0, 100);
      if (province.foodSecurity > 70 && province.prosperity > 55 && province.devastation < 25) {
        province.population = round2(province.population + Math.max(0.1, province.population * 0.0025));
      }
    } else {
      const deficit = 1 - ratio;
      province.foodSecurity = PhaserMathClamp(province.foodSecurity - 10 - deficit * 20, 0, 100);
      province.unrest = PhaserMathClamp(province.unrest + 3 + deficit * 7, 0, 100);
      province.prosperity = PhaserMathClamp(province.prosperity - 2 - deficit * 5, 0, 100);
      province.loyalty = PhaserMathClamp(province.loyalty - deficit * 3, 0, 100);
      if (deficit > 0.25) province.population = round2(Math.max(5, province.population - province.population * deficit * 0.006));
      ledger.notes.push(`Food shortage: ${round2(remainingNeed)} units unmet.`);
      if (province.owner === GameState.instance.requireCampaign().playerFactionId && deficit > 0.35) {
        GameState.instance.log(`${DataRegistry.instance.provinces[province.id]?.name} is suffering a serious food shortage.`, 'danger');
      }
    }

    const monthlyUnrest = ModifierManager.instance.provinceUnrestDelta(province.owner, province);
    province.unrest = PhaserMathClamp(province.unrest + monthlyUnrest, 0, 100);
    province.prosperity = PhaserMathClamp(province.prosperity + ModifierManager.instance.forFaction(province.owner).prosperityMonthlyDelta, 0, 100);
    ledger.populationChange = round2(province.population - priorPopulation);
    this.rescalePopulationGroups(province, priorPopulation);
  }

  private updatePrices(province: ProvinceState, ledger: ProvinceEconomyLedger) {
    let stability = 0;
    for (const buildingId of province.buildings) {
      const building = DataRegistry.instance.buildings[buildingId];
      stability += (building?.effects?.priceStability ?? 0) * (province.buildingLevels[buildingId] ?? 1);
    }

    for (const good of DataRegistry.instance.goodList()) {
      const target = this.targetStock(province, good.id);
      const stock = province.storage[good.id] ?? 0;
      const scarcity = PhaserMathClamp(target / Math.max(1, stock), 0.45, 2.8);
      const pressure = 1 + (scarcity - 1) * Math.max(0.2, 1 - stability);
      const price = round2(good.basePrice * pressure * (1 + province.corruption / 300));
      province.marketPrices[good.id] = price;
      ledger.prices[good.id] = price;
    }
  }

  private collectRevenue(province: ProvinceState, ledger: ProvinceEconomyLedger) {
    const campaign = GameState.instance.requireCampaign();
    const owner = campaign.factions[province.owner];
    if (!owner) return;
    const modifiers = ModifierManager.instance.forFaction(province.owner);
    let buildingTaxEfficiency = 0;
    for (const buildingId of province.buildings) {
      const building = DataRegistry.instance.buildings[buildingId];
      buildingTaxEfficiency += (building?.effects?.taxEfficiency ?? 0) * (province.buildingLevels[buildingId] ?? 1);
    }

    const taxablePopulation = province.populationGroups.peasants * 0.45
      + province.populationGroups.artisans * 1.1
      + province.populationGroups.merchants * 2.2
      + province.populationGroups.nobles * 1.4;
    const gross = Math.max(0, taxablePopulation * 0.055 * province.taxRate * (province.prosperity / 70) * (province.loyalty / 75));
    const autonomyLoss = gross * (province.autonomy / 100);
    const afterAutonomy = gross - autonomyLoss;
    const corruptionLoss = afterAutonomy * (province.corruption / 100);
    const tax = round2(Math.max(0, (afterAutonomy - corruptionLoss) * modifiers.taxMultiplier * (1 + buildingTaxEfficiency)));
    ledger.corruptionLoss = round2(corruptionLoss);
    ledger.taxIncome = tax;
    owner.treasury = round2(Math.max(0, owner.treasury + tax - ledger.maintenance));

    if (ledger.maintenance > tax * 1.5) ledger.notes.push('Building maintenance exceeds provincial tax income.');
    if (province.taxRate > 1.2) {
      province.unrest = PhaserMathClamp(province.unrest + (province.taxRate - 1.2) * 2.5, 0, 100);
      province.loyalty = PhaserMathClamp(province.loyalty - (province.taxRate - 1.2), 0, 100);
    }
    province.corruption = PhaserMathClamp(province.corruption + 0.15 + province.autonomy / 500 - (province.roadLevel ?? 0) * 0.08, 0, 100);
  }

  private applyCapacityAndEffects(province: ProvinceState, ledger: ProvinceEconomyLedger) {
    const capacity = this.storageCapacity(province.id);
    const stored = Object.values(province.storage).reduce((sum, amount) => sum + Math.max(0, amount), 0);
    if (stored > capacity) {
      const overflowRatio = (stored - capacity) / stored;
      for (const good of Object.keys(province.storage)) province.storage[good] = round2(province.storage[good] * (1 - overflowRatio));
      ledger.notes.push(`Storage overflow spoiled ${Math.round(overflowRatio * 100)}% of stockpiles.`);
    }
    province.workforce = this.workforceCapacity(province);
    if (ledger.workforceUsed > ledger.workforceCapacity * 0.96) ledger.notes.push('The province has almost no spare workforce.');
  }

  private targetStock(province: ProvinceState, goodId: string) {
    const foodNeed = Math.max(2, Math.ceil(province.population / 11));
    if (FOOD_GOODS.includes(goodId)) return foodNeed * (goodId === 'salt' ? 0.35 : 1.6);
    let inputNeed = 0;
    for (const buildingId of province.buildings) {
      const building = DataRegistry.instance.buildings[buildingId];
      inputNeed += (building?.inputs?.[goodId] ?? 0) * (province.buildingLevels[buildingId] ?? 1) * 2;
    }
    const commercialReserve = province.populationGroups.merchants * 0.08 + (province.portLevel ?? 0) * 2;
    return Math.max(4, inputNeed + commercialReserve);
  }

  private canExchange(source: ProvinceState, target: ProvinceState) {
    if (source.owner === target.owner) return true;
    const campaign = GameState.instance.requireCampaign();
    return campaign.factions[source.owner]?.treaties.trade?.includes(target.owner)
      || campaign.factions[target.owner]?.treaties.trade?.includes(source.owner)
      || false;
  }

  private buildingPriority(buildingId: string) {
    const category = DataRegistry.instance.buildings[buildingId]?.category;
    return category === 'agriculture' ? 0 : category === 'raw' ? 1 : category === 'infrastructure' ? 2 : category === 'craft' ? 3 : category === 'trade' ? 4 : 5;
  }

  private rescalePopulationGroups(province: ProvinceState, priorPopulation: number) {
    if (priorPopulation <= 0 || province.population === priorPopulation) return;
    const ratio = province.population / priorPopulation;
    let assigned = 0;
    const ids = Object.keys(province.populationGroups) as Array<keyof typeof province.populationGroups>;
    ids.forEach((id, index) => {
      const value = index === ids.length - 1
        ? Math.max(0, round2(province.population - assigned))
        : Math.max(0, round2(province.populationGroups[id] * ratio));
      province.populationGroups[id] = value;
      assigned += value;
    });
  }
}

function round2(value: number) {
  return Math.round(value * 100) / 100;
}

function PhaserMathClamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}
